import React, { useMemo, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { semantic_colors, shapes, spacing, text_styles } from '../../foundation';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';
import { IconArrowChevronDown } from '../icons/generated';
import { Input, InputProps } from '../input';
import { getInputHeightBySize } from '../input/styles';
import { useDropdownFloating, useDropdownInteractions } from '../dropdown/hooks';
import { DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT, XSMALL_DROPDOWN_COMPOSITE_MAX_HEIGHT } from './constants';
import { DropdownCompositeOptions } from './DropdownCompositeOptions';
import { DropdownCompositeDropdownOption, DropdownCompositeSize } from './types';
import { Divider } from '../divider';
import { css } from '@emotion/react';

export interface DropdownCompositeProps<OptionValue> {
  dropdownProps: DropdownCompositeDropdownOption<OptionValue>;
  inputProps: Pick<InputProps, 'width' | 'value' | 'placeholder' | 'onChange'>;
  /** @default 'medium' */
  size?: DropdownCompositeSize;
  /**
   * 선택된 필터가 적용된 강조 상태입니다.
   * @default false
   */
  highlighted?: boolean;
  disabled?: boolean;
  onSubmit?: ({ dropdownValue }: { dropdownValue?: OptionValue }) => void;
  onReset?: ({ dropdownValue }: { dropdownValue?: OptionValue }) => void;
  onOpen?: () => void;
}
export const DropdownComposite = <OptionValue,>({
  dropdownProps,
  inputProps,
  size = 'medium',
  highlighted = false,
  disabled,
  onOpen,
  onSubmit,
  onReset,
}: DropdownCompositeProps<OptionValue>) => {
  const [value, setValue] = useState<OptionValue | undefined>(dropdownProps.value);
  const [opened, setOpened] = useState(false);
  const [focused, setFocused] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const selectedIndex = dropdownProps.options.findIndex(({ value }) => value === dropdownProps.value);
  const selectedOption = dropdownProps.options[selectedIndex];

  const listElements = useMemo<Array<HTMLElement | null>>(
    () => Array.from({ length: dropdownProps.options.length }).fill(null) as null[],
    [dropdownProps.options],
  );
  const optionListRef = useRef<Array<HTMLElement | null>>(listElements);

  const typeaheadList = useMemo(
    () => dropdownProps.options.map(({ label }) => label?.toString() ?? null),
    [dropdownProps.options],
  );
  const typeaheadListRef = useRef<Array<string | null>>(typeaheadList);

  const onOpenChange = (open: boolean) => {
    if (open) {
      onOpen?.();
      setValue(dropdownProps.value);
      setFocusIndex(selectedIndex > -1 ? selectedIndex : 0);
    }
    setOpened(open);
  };

  const { refs, floatingStyles, context } = useDropdownFloating({
    open: opened,
    onOpenChange,
    optionsMatchRefWidth: false,
    maxHeight: size === 'xsmall' ? XSMALL_DROPDOWN_COMPOSITE_MAX_HEIGHT : DEFAULT_DROPDOWN_COMPOSITE_MAX_HEIGHT,
    autoPlacement: true,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    useClickProps: {
      enabled: !disabled,
    },
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
      disabledIndices: [],
    },
    useTypeaheadProps: {
      listRef: typeaheadListRef,
      activeIndex: focusIndex,
      onMatch: setFocusIndex,
    },
  });

  const labelRendered = useMemo(() => {
    if (selectedOption === undefined) return dropdownProps.placeholder;
    return selectedOption.label;
  }, [selectedOption, dropdownProps.placeholder]);

  const handleSetRefs = useCallback((node: HTMLElement | null, index: number) => {
    optionListRef.current[index] = node;
  }, []);
  const referenceProps = getReferenceProps({
    'className': 'pds-dropdown-composite-label',
    'aria-label': dropdownProps.placeholder,
    'width': dropdownProps.width,
    disabled,
    'selected': selectedIndex !== -1,
    'tabIndex': disabled ? -1 : 0,
  });

  return (
    <>
      <SC.Container
        ref={refs.setReference}
        disabled={disabled}
        editable={!!selectedOption?.editable}
        opened={opened}
        focused={focused}
        size={size}
        highlighted={highlighted}
        {...(!selectedOption?.editable ? referenceProps : {})}
      >
        <SC.DropdownValue
          width={dropdownProps.width}
          disabled={disabled}
          size={size}
          {...(selectedOption?.editable ? referenceProps : {})}
        >
          {labelRendered}
          <IconArrowChevronDown
            size={size === 'xsmall' ? 14 : 16}
            css={css`
              ${opened && 'transform: rotate(180deg)'};
            `}
            color={
              disabled
                ? semantic_colors.content.disabled
                : highlighted
                ? semantic_colors.accent.primary
                : semantic_colors.content.quaternary
            }
          />
        </SC.DropdownValue>
        <SC.Divider
          direction='vertical'
          length={size === 'xsmall' ? 16 : 22}
          color={highlighted && !disabled ? semantic_colors.accent.disabled : undefined}
        />
        <SC.Input
          {...inputProps}
          size={size}
          readOnly={!selectedOption?.editable}
          editable={!!selectedOption?.editable}
          highlighted={highlighted}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </SC.Container>
      <DropdownCompositeOptions<OptionValue>
        refs={refs}
        zIndex={dropdownProps.zIndex}
        options={dropdownProps.options}
        optionWidth={dropdownProps.width}
        setRefs={handleSetRefs}
        value={value}
        onClose={() => onOpenChange(false)}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        context={context}
        onChange={(value) => setValue(value)}
        setFocusIndex={setFocusIndex}
        floatingStyles={floatingStyles}
        onSubmit={() => onSubmit?.({ dropdownValue: value })}
        onReset={() => onReset?.({ dropdownValue: value })}
        size={size}
      />
    </>
  );
};

const SC = {
  Container: styled.div<{
    disabled?: boolean;
    editable?: boolean;
    opened?: boolean;
    focused?: boolean;
    size: DropdownCompositeSize;
    highlighted: boolean;
  }>`
    display: flex;
    box-sizing: border-box;
    height: ${({ size }) => getInputHeightBySize(size)}px;
    gap: ${({ size }) => (size === 'xsmall' ? spacing.spacing_2 : spacing.spacing_3)};
    padding: 0 ${({ size }) => (size === 'xsmall' ? spacing.spacing_2 : spacing.spacing_3)};
    align-items: center;
    ${shapes.border_radius.small};
    border: 1px solid ${semantic_colors.border.secondary};
    background-color: ${semantic_colors.background.surface};
    color: ${semantic_colors.content.primary};
    transition: all 0.1s linear;
    &:hover,
    &:active {
      border-color: ${semantic_colors.border.active};
    }
    ${({ editable }) =>
      !editable &&
      css`
        cursor: pointer;
      `}
    ${({ opened, focused }) =>
      (opened || focused) &&
      css`
        border-color: ${semantic_colors.border.active};
      `}
    ${({ highlighted }) =>
      highlighted &&
      css`
        color: ${semantic_colors.accent.primary};
        background-color: ${semantic_colors.accent.light};
        border-color: ${semantic_colors.accent.primary};
        &:hover,
        &:active {
          border-color: ${semantic_colors.accent.primary};
        }
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        color: ${semantic_colors.content.disabled};
        background-color: ${semantic_colors.background.disabled};
        border-color: ${semantic_colors.border.disabled};
        cursor: not-allowed;
        &:hover,
        &:active {
          border-color: ${semantic_colors.border.disabled};
        }
      `}
  `,
  DropdownValue: styled.div<{ width?: CSSValueWithLength; disabled?: boolean; size: DropdownCompositeSize }>`
    display: flex;
    height: 100%;
    min-width: 122px;
    flex: 1 0 auto;
    align-items: center;
    gap: ${spacing.spacing_1};
    justify-content: space-between;
    ${text_styles.Body_13_Regular};
    color: currentColor;
    ${({ width }) => buildCSSWithLength('width', width)};
    cursor: pointer;
    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
      `}
  `,
  Input: styled(Input, {
    shouldForwardProp: (prop) => !['editable', 'highlighted'].includes(prop),
  })<{ disabled?: boolean; editable?: boolean; highlighted: boolean; size: DropdownCompositeSize }>`
    height: 100%;
    padding-left: 0;
    border: none;
    ${({ editable }) =>
      !editable &&
      css`
        cursor: pointer;
        & > input {
          cursor: pointer;
        }
      `}
    &:hover,
    &:focus,
    &:focus-within {
      border: none;
    }
    ${({ highlighted, disabled }) =>
      highlighted &&
      !disabled &&
      css`
        color: ${semantic_colors.accent.primary};
        background-color: transparent;
        & > input {
          color: ${semantic_colors.accent.primary};
          -webkit-text-fill-color: ${semantic_colors.accent.primary};
          &::placeholder {
            color: ${semantic_colors.accent.primary};
          }
        }
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        color: ${semantic_colors.content.disabled};
        background-color: transparent;
        cursor: not-allowed;
        > input {
          color: ${semantic_colors.content.disabled};
          -webkit-text-fill-color: ${semantic_colors.content.disabled};
          cursor: not-allowed;
        }
      `}
  `,
  Divider: styled(Divider)`
    flex-shrink: 0;
  `,
};
